"use client";

import * as React from "react";
import { Activity, Check, Plus } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@hero-app/lib/utils";

export const buttonVariants = cva(
  "inline-flex items-center justify-center font-medium transition-colors text-[10px] leading-none tracking-[-0.2px]",
  {
    variants: {
      variant: {
        primary:
          "border-[0.5px] border-[#1a2026] bg-[#1a2026] text-white opacity-90 hover:opacity-100",
        secondary:
          "border-[0.5px] border-[#e3e6e8] bg-white/50 text-[#1a2026] hover:bg-white/70",
        outlined:
          "border-[0.5px] border-[#e3e6e8] bg-transparent text-[#506A76] hover:bg-white/30",
        disabled:
          "border-[0.5px] border-[#e3e6e8] bg-[#ececea] text-[#A2A6A6] cursor-not-allowed opacity-60",
      },
      icon: {
        enabled: "[&_svg]:opacity-100",
        disabled: "[&_svg]:opacity-30 pointer-events-none",
      },
      iconOnly: {
        true: "p-1",
        false: "gap-1 px-2 py-1",
      },
    },
    defaultVariants: {
      variant: "primary",
      icon: "enabled",
      iconOnly: false,
    },
  },
);

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;
export type ButtonIconState = NonNullable<
  VariantProps<typeof buttonVariants>["icon"]
>;
export type ButtonIconGlyph = "none" | "plus" | "activity" | "check";

export interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "disabled" | "icon"
    >,
    Omit<VariantProps<typeof buttonVariants>, "icon"> {
  /** Icon slot state — enabled shows icon at full opacity, disabled mutes it. */
  icon?: ButtonIconState;
  /** Serializable icon id for the Soffi editor runtime (maps to a built-in glyph). */
  iconGlyph?: ButtonIconGlyph;
  /** Optional icon element (e.g. Lucide icon). Ignored when `iconGlyph` is set. */
  iconElement?: React.ReactNode;
  /** Native disabled state; also applied when `variant="disabled"`. */
  disabled?: boolean;
}

const BUTTON_RADIUS = "clamp(2px, 0.28vw, 4px)";

const ICON_GLYPHS: Record<
  Exclude<ButtonIconGlyph, "none">,
  React.ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  plus: Plus,
  activity: Activity,
  check: Check,
};

/** Classes the editor runtime toggles via manifest `cvaVariants.iconGlyph`. */
const EDITOR_GLYPH_CLASS_NAMES: readonly ButtonIconGlyph[] = [
  "plus",
  "activity",
  "check",
  "none",
];

function readEditorGlyphFromClassList(
  classList: DOMTokenList,
): ButtonIconGlyph | null {
  for (const name of EDITOR_GLYPH_CLASS_NAMES) {
    if (classList.contains(name)) return name;
  }
  return null;
}

function resolveIconElement(
  iconGlyph: ButtonIconGlyph | undefined,
  iconElement: React.ReactNode | undefined,
): React.ReactNode | null {
  if (iconGlyph && iconGlyph !== "none") {
    const Icon = ICON_GLYPHS[iconGlyph];
    return <Icon className="size-2.5 shrink-0" strokeWidth={2} />;
  }
  return iconElement ?? null;
}

/**
 * Soffi button — primary, secondary, outlined, disabled, and icon enable/disable.
 */
export function Button({
  variant = "primary",
  icon = "enabled",
  iconGlyph,
  iconOnly = false,
  iconElement,
  disabled: disabledProp,
  className,
  style,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [editorGlyph, setEditorGlyph] = React.useState<ButtonIconGlyph | null>(null);

  React.useEffect(() => {
    setEditorGlyph(null);
  }, [iconGlyph]);

  React.useLayoutEffect(() => {
    const el = buttonRef.current;
    if (!el) return;

    const sync = () => {
      const fromDom = readEditorGlyphFromClassList(el.classList);
      if (fromDom) setEditorGlyph(fromDom);
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const isDisabled = disabledProp || variant === "disabled";
  const effectiveGlyph = editorGlyph ?? iconGlyph;
  const resolvedIcon = resolveIconElement(
    effectiveGlyph,
    editorGlyph != null ? undefined : iconElement,
  );

  return (
    <button
      ref={buttonRef}
      type={type}
      disabled={isDisabled}
      data-soffi-icon-glyph={effectiveGlyph ?? "none"}
      className={cn(
        buttonVariants({ variant, icon, iconOnly }),
        isDisabled && variant !== "disabled" && "cursor-not-allowed opacity-60",
        className,
      )}
      style={{ borderRadius: BUTTON_RADIUS, ...style }}
      {...props}
    >
      {resolvedIcon != null ? (
        <span className="inline-flex shrink-0 items-center [&_svg]:size-2.5">
          {resolvedIcon}
        </span>
      ) : null}
      {!iconOnly ? children : resolvedIcon == null ? children : null}
    </button>
  );
}
